import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import api from "../lib/api";

const Cart = () => {
    const { items, updateQuantity, removeItem, subtotal } = useCart();
    const navigate = useNavigate();
    // Free-shipping config comes from CMS (Admin → Shipping settings).
    // Admin can turn the whole feature OFF via `shipping.free_enabled`.
    // Until we've heard back from the API, hide the "Add $X for free
    // shipping" nudge so we never over-promise something the admin has
    // just disabled.
    const [freeEnabled, setFreeEnabled] = useState(null);
    const [freeThreshold, setFreeThreshold] = useState(75);
    useEffect(() => {
        api.get("/content").then(({ data }) => {
            const enabled = String(data?.["shipping.free_enabled"] ?? "true").toLowerCase() !== "false";
            const thr = parseFloat(data?.["shipping.free_over"] ?? 75);
            setFreeEnabled(enabled);
            setFreeThreshold(Number.isFinite(thr) && thr >= 0 ? thr : 75);
        }).catch(() => { setFreeEnabled(true); /* safe default matches server */ });
    }, []);
    const freeQualifies = freeEnabled && subtotal >= freeThreshold;
    const shipping = subtotal === 0 ? 0 : (freeQualifies ? 0 : 7.5);
    const total = subtotal + shipping;
    // Only nudge to add more when free shipping is actually available.
    const showFreeShippingNudge = freeEnabled === true && subtotal > 0 && subtotal < freeThreshold;

    if (items.length === 0) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="cart-empty">
                <ShoppingBag className="w-12 h-12 mx-auto text-[#4A5568] mb-6" strokeWidth={1.5} />
                <h1 className="font-heading text-3xl text-[#1C252E] font-medium mb-3">Your cart is empty</h1>
                <p className="text-[#4A5568] mb-8">Browse the collection — every purchase supports the cause.</p>
                <Link
                    to="/shop"
                    data-testid="cart-empty-shop-link"
                    className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-8 py-4 font-medium transition-all"
                >
                    Shop products
                </Link>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 lg:py-16" data-testid="cart-page">
            <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight mb-12">
                Your cart
            </h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-4">
                    {items.map((item) => (
                        <div
                            key={item.product_id}
                            data-testid={`cart-item-${item.product_id}`}
                            className="flex gap-4 sm:gap-6 bg-white border border-black/5 rounded-2xl p-4 sm:p-6 items-center"
                        >
                            <img
                                src={item.image_url}
                                alt={item.name}
                                className="w-20 h-20 sm:w-28 sm:h-28 object-cover rounded-xl bg-[#F4F1EA] flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                                <p className="font-heading text-base sm:text-lg text-[#1C252E] font-medium truncate">
                                    {item.name}
                                </p>
                                <p className="text-sm text-[#4A5568]">${item.price.toFixed(2)} each</p>
                                <div className="flex items-center gap-3 mt-3">
                                    <div className="flex items-center bg-[#F4F1EA] rounded-full">
                                        <button
                                            onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                                            data-testid={`cart-item-minus-${item.product_id}`}
                                            className="p-2"
                                            aria-label="decrease"
                                        >
                                            <Minus className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        </button>
                                        <span className="w-8 text-center text-sm font-medium" data-testid={`cart-item-qty-${item.product_id}`}>
                                            {item.quantity}
                                        </span>
                                        <button
                                            onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                                            data-testid={`cart-item-plus-${item.product_id}`}
                                            className="p-2"
                                            aria-label="increase"
                                        >
                                            <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => removeItem(item.product_id)}
                                        data-testid={`cart-item-remove-${item.product_id}`}
                                        className="p-2 text-[#4A5568] hover:text-[#E53935] transition-colors"
                                        aria-label="remove"
                                    >
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </div>
                            </div>
                            <p className="font-heading text-lg text-[#1C252E] font-medium" data-testid={`cart-item-total-${item.product_id}`}>
                                ${(item.price * item.quantity).toFixed(2)}
                            </p>
                        </div>
                    ))}
                </div>

                <div className="bg-white border border-black/5 rounded-2xl p-6 sm:p-8 h-fit sticky top-24">
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium mb-6">Summary</h2>
                    <div className="space-y-3 text-sm">
                        <div className="flex justify-between text-[#4A5568]">
                            <span>Subtotal</span>
                            <span className="text-[#1C252E] font-medium" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-[#4A5568]">
                            <span>Shipping</span>
                            <span className="text-[#1C252E] font-medium" data-testid="cart-shipping">
                                {shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}
                            </span>
                        </div>
                        {showFreeShippingNudge && (
                            <p className="text-xs text-[#E53935] pt-1" data-testid="cart-free-shipping-nudge">
                                Add ${(freeThreshold - subtotal).toFixed(2)} more for free shipping
                            </p>
                        )}
                    </div>
                    <div className="border-t border-black/10 my-6"></div>
                    <div className="flex justify-between mb-8">
                        <span className="font-heading text-lg text-[#1C252E] font-medium">Total</span>
                        <span className="font-heading text-2xl text-[#1C252E] font-medium" data-testid="cart-total">${total.toFixed(2)}</span>
                    </div>
                    <button
                        onClick={() => navigate("/checkout")}
                        data-testid="cart-checkout-btn"
                        className="w-full bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full py-4 font-medium transition-all hover:-translate-y-0.5"
                    >
                        Proceed to checkout
                    </button>
                    <Link
                        to="/shop"
                        data-testid="cart-continue-shopping-link"
                        className="block text-center mt-4 text-sm text-[#4A5568] hover:text-[#E53935]"
                    >
                        Continue shopping
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Cart;
